import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ExterneFreimeldung, FreimeldungLink } from '../types';
import {
    offeneJeItem as gruppiereOffene,
    istEndgueltigerEntscheidungsfehler,
    zaehleOffene,
} from '../services/freimeldungLogic';
import {
    entscheideFreimeldungMitBudget,
    entscheidungsFehlerCode,
    fetchFreimeldungLinks,
    fetchFreimeldungen,
} from '../services/freimeldungService';

/**
 * Die externen Freimeldungen EINES Projekts fuer den Innendienst
 * (docs/freimeldung-online-plan.md §F).
 *
 * KEIN REALTIME, BEWUSST. Eine Meldung von aussen ist kein Ereignis, auf das
 * die Begehungsliste zucken muesste: der Koordinator entscheidet sie im
 * eigenen Takt, und ein Abonnement, das waehrend der Bearbeitung neu
 * einsortiert, riesse ihm die Karte unter der Hand weg. Nachgeladen wird beim
 * Oeffnen des Moduls, nach jeder Entscheidung und auf Knopfdruck
 * („Aktualisieren" im FreimeldungPanel).
 *
 * FEHLER LANDEN IM ZUSTAND, NICHT IM RENDER. `fetchFreimeldungen` liefert bei
 * fehlender Migration von sich aus eine leere Liste (fail-open), aber ein
 * echter Fehler — kein Netz, RLS — darf die Begehungsliste nicht mit sich
 * reissen. Deshalb faengt der Hook alles und legt den Klartext in `fehler`.
 * `entscheiden` wirft aus demselben Grund nie: der Aufrufer ist ein
 * onClick-Handler in einer Mangelkarte. Es SCHWEIGT aber auch nicht — es
 * liefert ein Ergebnis mit Befund, damit ein Aufrufer mit Warteschlange den
 * Fehlschlag nicht fuer einen Erfolg haelt UND einen Keil von einem
 * wiederholbaren Fehler unterscheiden kann (siehe
 * `FreimeldungenZustand.entscheiden`).
 *
 * ZWEI FEHLERTOEPFE, UND ZWAR GETRENNT. `fehler` gehoert dem LADEN und wird
 * von jedem geglueckten Abruf geloescht. Eine gescheiterte ENTSCHEIDUNG darf
 * das nicht treffen: `entscheiden` laedt in seinem `finally` immer nach, und
 * genau dieses Nachladen loeschte den eben gesetzten Fehler wieder — der
 * Koordinator klickte „Bestätigen", nichts geschah, und nichts sagte ihm
 * warum. Deshalb liegt er in `entscheidungsFehler`, den das Nachladen nicht
 * anfasst.
 *
 * DER TOPF LEERT SICH NICHT VON SELBST. Geloescht wird er nur auf Zuruf
 * (`entscheidungsFehlerLoeschen`, `entscheidungsHinweisSetzen('')`) oder beim
 * Projektwechsel — NICHT mehr von der naechsten geglueckten Entscheidung. Der
 * Grund steht in der Warteschlange des SiGeKoInspectionModule: sie entscheidet
 * mehrere Auftraege NACHEINANDER in EINEM Lauf, und ein Erfolg hinter einem
 * Fehlschlag loeschte dessen Klartext, bevor ihn jemand lesen konnte — der
 * Koordinator sah nach einem halb gescheiterten Lauf eine Karte ohne jeden
 * Hinweis. Wer den Lauf fuehrt, fasst am Ende zusammen und raeumt selbst weg.
 *
 * Der Ladestand traegt das Projekt, fuer das er gilt (Muster
 * useModuleActivity): beim Projektwechsel zeigt das Modul so nie die Meldungen
 * des vorigen Projekts.
 */
/**
 * Was aus einer Entscheidung geworden ist — mehr als ein Ja/Nein, weil die
 * Warteschlange im SiGeKoInspectionModule aus einem „Nein" zwei verschiedene
 * Schluesse ziehen muss.
 *
 * `{ ok: true }` — der RPC ist durchgelaufen, die Meldung steht entschieden in
 * der Datenbank. Der Auftrag darf aus der Schlange.
 *
 * `{ ok: false, endgueltig: true }` — DER KEIL. Die Meldung wurde inzwischen
 * anders entschieden (P0001), es gibt sie nicht mehr (P0002), die Eingabe ist
 * abgelehnt (22023) oder RLS verweigert (42501). Ein weiterer Versuch aendert
 * daran nichts; der Auftrag muss aus der Schlange und der Koordinator den
 * Grund erfahren — sonst fragte die 30-Sekunden-Uhr bis zum Neuladen der Seite
 * erfolglos nach, und niemand saehe es.
 *
 * `{ ok: false, endgueltig: false }` — wiederholbar (kein Netz,
 * Zeitueberschreitung, alles Uebrige). Der Auftrag bleibt eingereiht, der
 * naechste Lauf versucht es erneut.
 */
export type FreimeldungEntscheidungErgebnis =
    | { ok: true }
    | {
          ok: false;
          /** SQLSTATE der Datenbank, falls der Fehler einen trug. */
          code: string | null;
          /** Steht fest: ein weiterer Versuch aendert nichts mehr. */
          endgueltig: boolean;
          /** Der Klartext fuer den Bildschirm — derselbe, der in `entscheidungsFehler` liegt. */
          klartext: string;
      };

export interface FreimeldungenZustand {
    /** Alle Meldungen des Projekts, juengste zuerst (auch entschiedene). */
    freimeldungen: ExterneFreimeldung[];
    /** Nur die OFFENEN, nach Mangel-Id gruppiert; Maengel ohne Meldung fehlen. */
    offeneJeItem: Map<string, ExterneFreimeldung[]>;
    /** Wie viele Meldungen auf eine Entscheidung warten. */
    offene: number;
    /** Ein Link je versendetem Bericht, neueste Berichtsnummer zuerst. */
    links: FreimeldungLink[];
    laedt: boolean;
    /** Klartext des letzten LADE-Fehlers; `null`, solange alles glatt lief. */
    fehler: string | null;
    /**
     * Klartext der letzten gescheiterten ENTSCHEIDUNG. Ueberlebt das
     * Nachladen und wird dort angezeigt, wo entschieden wurde (Mangelkarte,
     * Panel-Kopf).
     */
    entscheidungsFehler: string | null;
    /** Raeumt `entscheidungsFehler` weg (z. B. beim Schliessen eines Hinweises). */
    entscheidungsFehlerLoeschen: () => void;
    /**
     * Setzt denselben Klartext von aussen.
     *
     * NICHT JEDER FEHLSCHLAG IST EIN GESCHEITERTER RPC. Wer die Entscheidung
     * ueber eine Warteschlange fuehrt (SiGeKoInspectionModule), scheitert
     * schon davor: der Nachweis, dass der Mangel serverseitig geschlossen ist,
     * bleibt aus, oder der Auftrag war gar nicht einzureihen. Der RPC lief
     * dann nie — `entscheiden` hat nichts zu melden, und ohne diesen Setzer
     * bliebe die Sache still: der Koordinator klickte „Bestätigen", und die
     * Karte saehe hinterher aus wie vorher.
     *
     * Der Text landet in demselben Topf, weil er an dieselbe Stelle gehoert
     * (Mangelkarte, Panel-Kopf) und dieselbe Lebensdauer hat: das Nachladen
     * ruehrt ihn nicht an, der Projektwechsel und ein leerer Text raeumen ihn
     * weg.
     */
    entscheidungsHinweisSetzen: (text: string) => void;
    reload: () => void;
    /**
     * Entscheidet die Meldung und sagt, WAS daraus geworden ist.
     *
     * `{ ok: true }` heisst: der RPC ist durchgelaufen, die Meldung steht
     * entschieden in der Datenbank. Sonst steht sie weiter „offen" — der
     * Klartext liegt im Ergebnis UND — solange die Ansicht noch steht und
     * noch dasselbe Projekt zeigt — in `entscheidungsFehler`, und
     * `endgueltig` sagt, ob ein weiterer Versuch ueberhaupt noch lohnt. DAS
     * ERGEBNIS KOMMT IMMER: es ist die einzige Auskunft, die auch nach einem
     * Projektwechsel oder dem Demontieren noch ankommt.
     *
     * DAS ERGEBNIS IST KEINE ZIERDE. Wirft die Funktion nicht und liefert sie
     * auch nichts, sieht ein Aufrufer, der die Entscheidung in einer
     * Warteschlange fuehrt (SiGeKoInspectionModule), einen Fehlschlag wie
     * einen Erfolg aus: er nahm den Auftrag aus der Schlange, die Nachfass-Uhr
     * hielt mangels Inhalt an, und die Entscheidung war fort, ohne je gebucht
     * worden zu sein. Und ohne `endgueltig` bliebe der umgekehrte Fall stehen:
     * ein Auftrag, der NIE durchgehen kann, in einer Schlange, die ihn alle 30
     * Sekunden neu versucht.
     */
    entscheiden: (
        id: string,
        entscheidung: 'bestaetigt' | 'abgelehnt',
        kommentar?: string,
    ) => Promise<FreimeldungEntscheidungErgebnis>;
}

const LEER: ExterneFreimeldung[] = [];
const KEINE_LINKS: FreimeldungLink[] = [];

const fehlerText = (e: unknown): string =>
    e instanceof Error ? e.message : String(e ?? 'Unbekannter Fehler');

export const useFreimeldungen = (projectId: string | undefined | null): FreimeldungenZustand => {
    const id = (projectId || '').trim();
    const [geladen, setGeladen] = useState<{
        fuer: string;
        meldungen: ExterneFreimeldung[];
        links: FreimeldungLink[];
    }>({ fuer: '', meldungen: LEER, links: KEINE_LINKS });
    const [laedt, setLaedt] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [entscheidungsFehler, setEntscheidungsFehler] = useState<string | null>(null);
    const [lauf, setLauf] = useState(0);

    const reload = useCallback(() => setLauf((n) => n + 1), []);
    const entscheidungsFehlerLoeschen = useCallback(() => setEntscheidungsFehler(null), []);
    // Leerer Text raeumt weg statt eine leere Zeile anzuzeigen.
    const entscheidungsHinweisSetzen = useCallback(
        (text: string) => setEntscheidungsFehler((text || '').trim() || null),
        [],
    );

    /**
     * DER HOOK SCHREIBT NUR IN EINE ANSICHT, DIE ES NOCH GIBT — UND NUR IN DIE
     * DES RICHTIGEN PROJEKTS.
     *
     * Zwischen dem Klick auf „Bestätigen" und der Antwort des Servers liegen
     * bis zu acht Sekunden (`FREIMELDUNG_ZEITBUDGET_MS`). In dieser Luecke
     * kann die Mangelkarte geschlossen und das Projekt gewechselt sein. Die
     * Setter liefen bisher trotzdem: `setEntscheidungsFehler` schrieb den
     * Fehler des ALTEN Projekts in die Ansicht des neuen, und der `reload()`
     * im `finally` stiess ueber `setLauf` einen Ladelauf an, dessen Ergebnis
     * niemand mehr brauchte. Die Wachen des SiGeKoInspectionModule greifen
     * dafuer zu spaet — sie laufen erst, NACHDEM der Hook gesetzt hat.
     *
     * Deshalb hier, im Hook selbst: `montiert` sagt, ob es die Ansicht noch
     * gibt, `projektJetzt` sagt, fuer welches Projekt sie steht. Der Ref wird
     * beim Rendern nachgezogen und nicht im Effekt (Muster
     * `freimeldungProjektJetzt` im SiGeKoInspectionModule): ein Effekt laeuft
     * erst nach dem Anstrich, und genau in dieser Luecke traefe ein Klick noch
     * das alte Projekt.
     *
     * DAS ERGEBNIS BLEIBT DAVON UNBERUEHRT: `entscheiden` liefert seinen
     * Befund auch dann zurueck, wenn es nichts mehr setzen darf — die
     * Warteschlange im Modul muss wissen, ob der RPC durchlief, sonst buchte
     * ihn der naechste Lauf ein zweites Mal.
     */
    const montiert = useRef(true);
    useEffect(() => {
        montiert.current = true;
        return () => { montiert.current = false; };
    }, []);
    const projektJetzt = useRef<string>(id);
    projektJetzt.current = id;
    /** Darf ein Rueckweg, der fuer `fuerProjekt` losgeschickt wurde, noch setzen? */
    const darfSchreiben = useCallback(
        (fuerProjekt: string) => montiert.current && projektJetzt.current === fuerProjekt,
        [],
    );

    // NUR beim Projektwechsel, nicht bei jedem Nachladen: haengte das
    // Loeschen an `lauf`, raeumte der Reload aus `entscheiden` den Fehler
    // sofort wieder weg — genau der Befund, um den es hier geht.
    useEffect(() => {
        setEntscheidungsFehler(null);
    }, [id]);

    useEffect(() => {
        if (!id || id === 'temp-id') {
            setLaedt(false);
            return;
        }
        let aktiv = true;
        setLaedt(true);
        void (async () => {
            try {
                // Beide Abfragen zusammen: Links und Meldungen gehoeren in
                // derselben Ansicht nebeneinander, zwei Runden liessen das
                // Panel zwischendurch halb gefuellt stehen.
                const [meldungen, links] = await Promise.all([
                    fetchFreimeldungen(id),
                    fetchFreimeldungLinks(id),
                ]);
                // `aktiv` faellt schon beim Aufraeumen des Effekts (Wechsel,
                // Demontage); `darfSchreiben` stellt dieselbe Frage noch einmal
                // ausdruecklich — es ist die Wache, die `entscheiden` unten
                // braucht, wo kein Effekt aufraeumt.
                if (!aktiv || !darfSchreiben(id)) return;
                setGeladen({ fuer: id, meldungen, links });
                setFehler(null);
            } catch (e) {
                if (!aktiv || !darfSchreiben(id)) return;
                console.warn('Freimeldungen konnten nicht geladen werden.', e);
                setFehler(fehlerText(e));
            } finally {
                if (aktiv && darfSchreiben(id)) setLaedt(false);
            }
        })();
        return () => {
            aktiv = false;
        };
    }, [id, lauf, darfSchreiben]);

    const entscheiden = useCallback(
        async (
            freimeldungId: string,
            entscheidung: 'bestaetigt' | 'abgelehnt',
            kommentar?: string,
        ): Promise<FreimeldungEntscheidungErgebnis> => {
            // VOR dem `await` festgehalten: danach sagt der Ref schon das neue
            // Projekt, und die Antwort liesse sich keinem mehr zuordnen.
            const fuerProjekt = projektJetzt.current;
            try {
                // GEGEN DIE UHR (8 s, `FREIMELDUNG_ZEITBUDGET_MS`). Ein RPC,
                // der nie antwortet, liesse den Aufrufer ewig warten — und der
                // Aufrufer ist im SiGeKoInspectionModule eine Warteschlange,
                // die waehrenddessen gesperrt bleibt. Der Ablauf wirft und
                // landet unten im `catch`: Klartext an der Karte, ein
                // ausdrueckliches Nein fuer den Aufrufer, der Auftrag bleibt
                // eingereiht.
                await entscheideFreimeldungMitBudget(freimeldungId, entscheidung, kommentar);
                // KEIN `setEntscheidungsFehler(null)` MEHR. Ein Erfolg loescht
                // den Klartext nicht: in EINEM Lauf der Warteschlange folgen
                // mehrere Entscheidungen aufeinander, und ein Erfolg hinter
                // einem Fehlschlag riss dessen Hinweis weg, bevor ihn jemand
                // lesen konnte. Weggeraeumt wird von dem, der den Lauf fuehrt
                // (`entscheidungsHinweisSetzen('')`), vom Projektwechsel oder
                // auf Zuruf.
                return { ok: true };
            } catch (e) {
                console.error('Entscheidung über die Freimeldung fehlgeschlagen', e);
                const klartext = fehlerText(e);
                // NICHT in `fehler` — das Nachladen unten macht ihn wieder null.
                // UND NUR, WENN ES DIE ANSICHT NOCH GIBT: nach einem
                // Projektwechsel gehoerte dieser Satz zum vorigen Projekt und
                // stuende im neuen als dessen Befund.
                if (darfSchreiben(fuerProjekt)) setEntscheidungsFehler(klartext);
                // Der Aufrufer erfaehrt den Fehlschlag, statt ihn aus dem
                // stillen Durchlaufen erraten zu muessen: nichts wurde
                // gebucht, die Meldung steht weiter offen. Und er erfaehrt, ob
                // ein weiterer Versuch je durchgehen kann — der SQLSTATE haengt
                // am Fehler (`FreimeldungEntscheidungFehler`), nicht nur im
                // Satz.
                const code = entscheidungsFehlerCode(e);
                return {
                    ok: false,
                    code,
                    endgueltig: istEndgueltigerEntscheidungsfehler(code),
                    klartext,
                };
            } finally {
                // Auch nach einem Fehler: die Liste soll den Stand der
                // Datenbank zeigen, nicht den erhofften. Aber nur fuer eine
                // Ansicht, die es noch gibt und die noch dieses Projekt zeigt
                // — sonst setzte `setLauf` in eine demontierte Ansicht oder
                // riefe einen Ladelauf fuer ein Projekt ab, das niemand mehr
                // sieht. Das Ergebnis oben ist da laengst unterwegs.
                if (darfSchreiben(fuerProjekt)) reload();
            }
        },
        [reload, darfSchreiben],
    );

    // Erst nach dem Wechsel passend: solange das neue Projekt laedt, ist die
    // Liste leer statt fremd.
    const freimeldungen = geladen.fuer === id ? geladen.meldungen : LEER;
    const links = geladen.fuer === id ? geladen.links : KEINE_LINKS;

    const gruppiert = useMemo(() => gruppiereOffene(freimeldungen), [freimeldungen]);
    const offene = useMemo(() => zaehleOffene(freimeldungen), [freimeldungen]);

    return {
        freimeldungen,
        offeneJeItem: gruppiert,
        offene,
        links,
        laedt,
        fehler,
        entscheidungsFehler,
        entscheidungsFehlerLoeschen,
        entscheidungsHinweisSetzen,
        reload,
        entscheiden,
    };
};
